import { useEffect } from "react";
import { APIProvider, Map, AdvancedMarker, Polyline, useMap, useApiLoadingStatus } from "@vis.gl/react-google-maps";

// Mapa de Google (Maps JavaScript API) para fijar/ajustar origen y destino.
// Mismo contrato de props que MapPicker (Leaflet), que se conserva como respaldo.
// El pan/zoom NO genera llamadas de API adicionales (solo la carga inicial del mapa).

const DEFAULT_CENTER = { lat: 3.705, lng: -76.29 }; // El Cerrito/Ginebra, Valle del Cauca
const DEFAULT_ZOOM = 13;

function Pin({ color, label }) {
  return (
    <div
      style={{ background: color, borderColor: color }}
      className="grid h-7 w-7 place-items-center rounded-full border-2 text-[11px] font-extrabold text-black shadow-lg"
    >
      {label}
    </div>
  );
}

// Ajusta vista: ambos puntos -> fitBounds; uno -> centrar con zoom 17; ninguno -> vista piloto.
function Viewport({ pickup, dropoff }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    const hasP = pickup?.lat != null;
    const hasD = dropoff?.lat != null;
    if (hasP && hasD) {
      const b = new window.google.maps.LatLngBounds();
      b.extend({ lat: pickup.lat, lng: pickup.lng });
      b.extend({ lat: dropoff.lat, lng: dropoff.lng });
      map.fitBounds(b, 80);
    } else if (hasP) {
      map.setCenter({ lat: pickup.lat, lng: pickup.lng });
      map.setZoom(17);
    } else if (hasD) {
      map.setCenter({ lat: dropoff.lat, lng: dropoff.lng });
      map.setZoom(17);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);
  return null;
}

// Si el SDK de Google falla (clave inválida, referrer no autorizado, etc.), avisa para caer a Leaflet.
function LoadWatcher({ onFailed }) {
  const status = useApiLoadingStatus();
  useEffect(() => {
    if (status === "FAILED") onFailed?.();
  }, [status, onFailed]);
  return null;
}

export const GoogleMapPicker = ({ apiKey, pickup, dropoff, mode, route, onPick, onMarkerMove, onFailed }) => {
  const routePath = Array.isArray(route) ? route.map(([lng, lat]) => ({ lat, lng })) : null;

  return (
    <div className="relative">
      <APIProvider apiKey={apiKey} language="es" region="CO">
        <LoadWatcher onFailed={onFailed} />
        <Map
          mapId="7db06d7632d50de5aa7172"
          style={{ width: "100%", height: 380 }}
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          onClick={(e) => {
            const ll = e.detail?.latLng;
            if (ll) onPick?.(mode, { lat: ll.lat, lng: ll.lng });
          }}
        >
          <Viewport pickup={pickup} dropoff={dropoff} />
          {routePath && routePath.length > 1 && (
            <Polyline path={routePath} strokeColor="#FF6A00" strokeOpacity={0.9} strokeWeight={4} />
          )}
          {pickup?.lat != null && (
            <AdvancedMarker
              position={{ lat: pickup.lat, lng: pickup.lng }}
              draggable
              onDragEnd={(e) => onMarkerMove?.("pickup", { lat: e.latLng.lat(), lng: e.latLng.lng() })}
            >
              <Pin color="#FF6A00" label="A" />
            </AdvancedMarker>
          )}
          {dropoff?.lat != null && (
            <AdvancedMarker
              position={{ lat: dropoff.lat, lng: dropoff.lng }}
              draggable
              onDragEnd={(e) => onMarkerMove?.("dropoff", { lat: e.latLng.lat(), lng: e.latLng.lng() })}
            >
              <Pin color="#34D399" label="B" />
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
      <div className="pointer-events-none absolute bottom-3 left-3 rounded-lg border border-[#27272A] bg-[#0d0d0f]/90 px-3 py-2 text-[11px] text-gray-300 backdrop-blur" data-testid="google-map-hint">
        {mode === "pickup" ? "Toca el mapa para fijar el ORIGEN (A) — arrastra el pin para ajustar" : "Toca el mapa para fijar el DESTINO (B) — arrastra el pin para ajustar"}
      </div>
    </div>
  );
};
